import React, { useState, useRef, useEffect } from 'react';
import { ICONS } from '../../constants/imageConstants';

const PullToRefresh = ({ onRefresh, children, threshold = 80 }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef(null);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const isRefreshingRef = useRef(false);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  const handleTouchStart = (e) => {
    if (containerRef.current && containerRef.current.scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
    } else {
      startY.current = 0;
    }
  };

  const handleTouchMove = (e) => {
    const scrollTop = containerRef.current?.scrollTop || 0;
    
    if (isRefreshingRef.current || (scrollTop > 0 && !isDragging.current)) return;

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff > 0 && scrollTop <= 0 && startY.current > 0) {
      if (!isDragging.current && diff > 5) {
        isDragging.current = true;
      }

      if (isDragging.current) {
        if (e.cancelable) e.preventDefault();
        
        const distance = Math.min(diff * 0.5, threshold * 1.5);
        setPullDistance(distance);
      }
    } else {
      if (isDragging.current) {
        isDragging.current = false;
        setPullDistance(0);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    
    isDragging.current = false;
    const dist = pullDistanceRef.current;
    
    if (dist >= threshold && !isRefreshingRef.current) {
      setIsRefreshing(true);
      setPullDistance(threshold);
      
      const refreshPromise = typeof onRefresh === 'function' ? onRefresh() : Promise.resolve();
      
      Promise.resolve(refreshPromise)
        .then(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        })
        .catch(()=> {
          setIsRefreshing(false);
          setPullDistance(0);
        });
    } else {
      setPullDistance(0);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const options = { passive: false };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, options);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onRefresh, threshold]);

  const refreshIndicatorStyle = {
    transform: `translateY(${Math.min(pullDistance, threshold + 20)}px) rotate(${pullDistance * 2}deg)`,
    opacity: Math.min(pullDistance / (threshold * 0.5), 1),
    transition: isDragging.current ? 'none' : 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  };

  const containerStyle = {
    transform: `translateY(${isRefreshing ? threshold : Math.min(pullDistance, threshold)}px)`,
    transition: isDragging.current ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
  };

  return (
    <div className="relative h-full w-full overflow-hidden flex flex-col">
      <div 
        className="absolute top-0 left-0 right-0 flex items-center justify-center z-10 pointer-events-none"
        style={{ height: threshold, marginTop: -threshold }}
      >
        <div 
          className="flex items-center justify-center bg-white rounded-full p-2.5 shadow-md border border-gray-100"
          style={refreshIndicatorStyle}
        >
          {isRefreshing ? (
            <div className="w-5 h-5 border-2 border-[var(--btn-primary-bg)] border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <img 
              src={ICONS.SYNC_STATUS} 
              alt="Refresh" 
              className="w-5 h-5 text-[var(--btn-primary-bg)]"
              style={{ 
                transform: `rotate(${pullDistance > threshold ? 180 : 0}deg)`, 
                transition: 'transform 0.2s',
                filter: 'brightness(0) saturate(100%) invert(33%) sepia(96%) saturate(408%) hue-rotate(94deg) brightness(93%) contrast(89%)'
              }}
            />
          )}
        </div>
      </div>
      
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto scrollbar-hide h-full w-full"
        style={containerStyle}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;